import React from 'react';
// @ts-ignore
import shieldAlt from '../assets/shield-check.svg';
// @ts-ignore
import fileAlt from '../assets/file-alt.svg';
// @ts-ignore
import checkCircle from '../assets/check-circle.svg';

const ICON_FILTER = 'brightness(0) saturate(100%) invert(21%) sepia(88%) saturate(1305%) hue-rotate(200deg) brightness(97%) contrast(105%)';

type Page = 'home' | 'search' | 'chatbot';

interface HomePageProps {
  onNavigate: (page: Page) => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onNavigate }) => {
  return (
    <div className="flex-1 bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-4xl w-full text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-[#E8EFFE] text-[#0943B5] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            🧠 Sistema de Gestión de Conocimiento con IA
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight"
          >
            Consulta inteligente de documentos<br />
            <span className="text-[#0943B5]">PQRSD</span>
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto">
            Accede a la base de conocimiento institucional mediante búsqueda semántica
            o interactúa directamente con nuestro asistente de IA especializado.
          </p>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-3xl w-full">
          {/* Card 1: Búsqueda */}
          <button
            onClick={() => onNavigate('search')}
            className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-[#0943B5]/40 transition-all duration-300 p-6 sm:p-8 text-left flex flex-col gap-5 cursor-pointer"
          >
            <div className="w-14 h-14 bg-[#E8EFFE] rounded-xl flex items-center justify-center group-hover:bg-[#0943B5] transition-colors duration-300">
              <svg className="w-7 h-7 text-[#0943B5] group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Búsqueda en Biblioteca
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Realiza búsquedas semánticas sobre la base documental de PQRSD.
                Aplica filtros por radicado, solicitante o número de entrada.
              </p>
            </div>
            <div className="mt-auto flex items-center gap-2 text-[#0943B5] font-medium text-sm group-hover:gap-3 transition-all duration-200">
              Ir a búsqueda
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          {/* Card 2: Chatbot */}
          <button
            onClick={() => onNavigate('chatbot')}
            className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-[#0943B5]/40 transition-all duration-300 p-6 sm:p-8 text-left flex flex-col gap-5 cursor-pointer"
          >
            <div className="w-14 h-14 bg-[#E8EFFE] rounded-xl flex items-center justify-center group-hover:bg-[#0943B5] transition-colors duration-300">
              <svg className="w-7 h-7 text-[#0943B5] group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Consulta Avanzada Chatbot
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Interactúa con nuestro asistente de IA especializado en normativa PQRSD.
                Obtén respuestas fundamentadas con cita de fuentes documentales.
              </p>
            </div>
            <div className="mt-auto flex items-center gap-2 text-[#0943B5] font-medium text-sm group-hover:gap-3 transition-all duration-200">
              Iniciar consulta
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>

        {/* Features row */}
        <div className="mt-12 sm:mt-16 w-full max-w-6xl px-6 md:px-16">
          <div className="flex divide-x divide-[#0943B5]">

            <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-3 pr-6">
              <img src={shieldAlt} alt="" aria-hidden="true" className="w-5 h-5 shrink-0" style={{ filter: ICON_FILTER }} />
              <span className="text-xs text-gray-500 text-center leading-snug">Búsqueda semántica con IA</span>
            </div>

            <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-3 px-6">
              <img src={fileAlt} alt="" aria-hidden="true" className="w-5 h-5 shrink-0" style={{ filter: ICON_FILTER }} />
              <span className="text-xs text-gray-500 text-center leading-snug">Respuestas fundamentadas en documentos</span>
            </div>

            <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-3 pl-6">
              <img src={checkCircle} alt="" aria-hidden="true" className="w-5 h-5 shrink-0" style={{ filter: ICON_FILTER }} />
              <span className="text-xs text-gray-500 text-center leading-snug">Cita automática de fuentes</span>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};
